import cc from 'classcat';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { whenLazyComponentsLoaded } from '#src/lib/tracked-lazy';

import { StickyComponentContext } from './StickyComponent/StickyComponentContext.js';
import styles from './PageWrapper.scss';

// Same state object when nothing changes, so React skips the re-render.
const reducer = (state, action) =>
  Object.keys(action).every((key) => state[key] === action[key])
    ? state
    : { ...state, ...action };

export function PageWrapper({ className, ...props }) {
  const [stickyContextValue, dispatch] = useReducer(reducer, {
    footerVisible: false,
  });

  // The footer reports its visibility as soon as it mounts. A context change
  // then reaches every lazy row that is still hydrating, and React discards
  // their server HTML (React error #421), so hold changes until they have
  // hydrated and apply only the latest one.
  const ready = useRef(false);
  const pending = useRef(null);
  useEffect(() => {
    let active = true;
    whenLazyComponentsLoaded().then(() => {
      if (!active) return;
      ready.current = true;
      if (pending.current) dispatch(pending.current);
    });
    return () => {
      active = false;
    };
  }, []);
  const setFooterVisible = useCallback((footerVisible) => {
    if (ready.current) dispatch({ footerVisible });
    else pending.current = { footerVisible };
  }, []);

  const value = useMemo(
    () => ({
      footerVisible: stickyContextValue.footerVisible,
      setFooterVisible,
    }),
    [stickyContextValue.footerVisible, setFooterVisible]
  );

  return (
    <StickyComponentContext.Provider value={value}>
      <div {...props} className={cc([styles.container, className])} />
    </StickyComponentContext.Provider>
  );
}
